import React, { useMemo } from 'react';
import TeamJersey from './TeamJersey';
import QuestionCard from './QuestionCard';
import { OptionPercentageStat } from './OptionPercentageStat';
import { createJerseyMap, type Team } from '../utils/jersey-resolver';
import { isOrphanCard, ORPHAN_CARD_CLASSES } from './orphan-card';
import { pickFlourishClass, PickSweep, usePickInteraction } from './pick-flourish';

interface TeamPickProps {
  questionId?: string;
  questionText: string;
  subtext?: string;
  teams: Team[];
  selectedTeam?: string;
  onTeamSelect: (teamId: string | undefined) => void;
  points: number;
  earnedPoints?: number;
  isCorrect?: boolean;
  disabled?: boolean;
  className?: string;
  questionNumber?: number;
  questionMetadata?: {
    dailyDouble?: boolean;
  };
}

const TeamPick: React.FC<TeamPickProps> = ({
  questionId,
  questionText,
  subtext,
  teams,
  selectedTeam,
  onTeamSelect,
  points,
  earnedPoints,
  isCorrect,
  disabled = false,
  className = '',
  questionNumber,
  questionMetadata,
}) => {
  // Debug logging
  console.log('[TeamPick] Render:', {
    questionId,
    selectedTeam,
    selectedTeamType: typeof selectedTeam,
    isUndefined: selectedTeam === undefined,
    teams: teams.map(t => ({ id: t.id, isSelected: selectedTeam === t.id })),
  });

  // Memoize team jersey data to prevent random changes and unnecessary re-renders
  const teamJerseyMap = useMemo(() => createJerseyMap(teams), [teams]);

  const isDailyDouble = questionMetadata?.dailyDouble || false;
  const anySelected = selectedTeam !== undefined;
  const { hasInteracted, interactionProps, arm } = usePickInteraction();

  return (
    <QuestionCard
      points={points}
      earnedPoints={earnedPoints}
      className={className}
      questionNumber={questionNumber}
      isDailyDouble={isDailyDouble}
      isCorrect={isCorrect}
      data-testid={questionId ? `question-${questionId}` : undefined}
      data-testtype="team"
    >
      {/* Subtext (if present) */}
      {subtext && (
        <span
          data-testid="team-subtext"
          className="text-[#ABABAB] font-light block text-center mt-2 text-sm"
        >
          {subtext}
        </span>
      )}

      {/* Question Text */}
      <span
        data-testid="team-question"
        className="text-text-primary font-medium block text-center mb-2 md:mb-4 text-sm"
      >
        {questionText}
      </span>

      {/* Team Selection */}
      <div
        className={`grid gap-2 md:gap-4 ${teams.length > 4 ? 'grid-cols-1' : 'grid-cols-2'}`}
        data-testid="team-selection"
      >
        {teams.map((team, index) => {
          const isSelected = selectedTeam === team.id;
          const hasResult = isCorrect !== undefined;
          const resultBorderClass =
            isSelected && hasResult
              ? isCorrect
                ? 'border-2 border-green-500 shadow-[0_0_15px_4px_rgba(34,197,94,0.4)]'
                : 'border-2 border-red-500 shadow-[0_0_15px_4px_rgba(239,68,68,0.4)]'
              : '';

          // Single column layout (more than 4 options)
          const isSingleColumn = teams.length > 4;
          const isOrphan = isOrphanCard({ index, total: teams.length, isSingleColumn });

          // The option content is pushed down to clear whatever is pinned to the top of the
          // box — the popularity badge (top-left) and the secondary logo (top-right). With
          // neither present, that clearance is dead height, and on a phone it costs a
          // question its place on screen.
          const hasTopOverlay =
            (team.popularityPct !== undefined && teams.length >= 2) || !!team.thirdImageUrl;

          // Daily Double styling logic
          const getBackgroundClass = () => {
            if (isSelected ) return 'bg-white';
            return isDailyDouble ? 'bg-[#000D1F]' : 'bg-[#121212]';
          };

          const getBorderClass = () => {
            if (hasResult) return resultBorderClass;

            if (isDailyDouble) {
              // Daily Double: blue border default, white on selected
              if (isSelected) return 'border border-white';
              return 'border border-[#0D54AD]';
            }

            // Normal: black border default, gray on selected
            if (isSelected) return 'border border-[#6F6F6F]';
            return 'border border-[#121212]';
          };

          const getHoverClass = () => {
            if (disabled || hasResult) return '';
            // Daily Double: white border on hover
            // Normal: gray border on hover
            return isDailyDouble ? 'hover:border-white' : 'hover:border-[#6F6F6F]';
          };

          return (
            <div
              key={team.id}
              className={`flex flex-col items-center justify-center ${
                isOrphan ? ORPHAN_CARD_CLASSES : ''
              }`}
            >
              <button
                {...interactionProps}
                onClick={() => {
                  arm();
                  console.log('[TeamPick] Button clicked:', {
                    questionId,
                    teamId: team.id,
                    isSelected,
                    willPassValue: isSelected ? undefined : team.id,
                    disabled,
                  });
                  if (!disabled) onTeamSelect(isSelected ? undefined : team.id);
                }}
                disabled={disabled}
                data-testid={`option-${team.id}`}
                data-testresult={
                  isSelected && hasResult ? (isCorrect ? 'correct' : 'incorrect') : undefined
                }
                data-selected={isSelected ? 'true' : 'false'}
                className={`
                  group peer w-full h-auto ${isSingleColumn ? 'min-h-[80px]' : 'min-h-[80px] md:min-h-[140px]'} md:p-6 p-2 rounded-xl transition-smooth ${isSingleColumn ? 'grid grid-cols-[auto_1fr_auto] gap-4 items-center' : 'flex flex-col items-center justify-center'} relative overflow-hidden
                  ${getBackgroundClass()}
                  ${getBorderClass()}
                  ${getHoverClass()}
                  ${pickFlourishClass({ isSelected, anySelected, hasResult, disabled, animate: hasInteracted })}
                  ${disabled && !hasResult ? 'cursor-not-allowed opacity-90' : ''}
                `}
              >
                {/* Popularity Percentage - Top Left */}
                {team.popularityPct !== undefined && teams.length >= 2 && (
                  <div className="absolute top-2 left-2">
                    <OptionPercentageStat percentage={team.popularityPct} isSelected={isSelected} />
                  </div>
                )}

                {isSingleColumn ? (
                  <>
                    {/* Left Column: Jersey (wide enough to not overlap percentage) */}
                    <div className="flex justify-center pl-8">
                      <TeamJersey size="sm" jerseyUrl={teamJerseyMap.get(team.id)} />
                    </div>

                    {/* Middle Column: Team Name */}
                    <div className="flex items-center min-w-0">
                      <div
                        className={`text-base font-medium ${isSelected ? 'text-black' : 'text-text-primary'}`}
                      >
                        {team.name}
                      </div>
                    </div>

                    {/* Right Column: Secondary Logo */}
                    {team.thirdImageUrl && (
                      <div className="flex justify-center">
                        <img
                          src={team.thirdImageUrl}
                          alt="Secondary logo"
                          className="w-6 h-6 object-contain"
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* Secondary Image - Top Right */}
                    {team.thirdImageUrl && (
                      <div className="absolute top-2 right-2 w-10 h-10">
                        <img
                          src={team.thirdImageUrl}
                          alt="Secondary logo"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    )}

                    <div
                      className={`flex flex-col items-center space-y-1 md:space-y-3 ${
                        hasTopOverlay ? 'mt-4 md:mt-6' : 'md:mt-6'
                      }`}
                    >
                      {/* Team Jersey Circle */}
                      <TeamJersey size="xs" sizeMd="md" jerseyUrl={teamJerseyMap.get(team.id)} />

                      {/* Team Name. The min-height reserves a second line so wrapped names
                          don't jump the layout — worth its height on desktop, not on a phone
                          where the grid already stretches both options to a common height. */}
                      <div
                        className={`${
                          team.name.split(' ').some(l => l.length > 14) ? 'text-sm' : 'text-base'
                        } md:min-h-12 ${isSelected  ? 'text-black' : 'text-text-primary'}`}
                      >
                        {team.name}
                      </div>

                    </div>
                  </>
                )}

                {!hasResult && <PickSweep active={isSelected} animate={hasInteracted} />}
              </button>

              {/* Live Value (shown during live scoring) */}
              {team.liveValue !== undefined && (
                <div
                  data-testid={`live-value-${team.id}`}
                  className="text-xs font-medium mt-1 text-[#00C853]"
                >
                  {team.liveValue}{team.liveStatType ? ` ${team.liveStatType}` : ''}
                </div>
              )}

            </div>
          );
        })}
      </div>
    </QuestionCard>
  );
};

export default TeamPick;
